import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, Loader2 } from 'lucide-react';
import AgentWorkspace from '../AgentWorkspace/AgentWorkspace';
import ProjectOverview from './ProjectOverview';
import AssistantsView from '../AssistantsView/AssistantsView';
import { WorkflowsView } from '../Workflows';
import RunsView from '../Workflows/RunsView';
import RunInspector from '../Workflows/RunInspector';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAssistantContext } from '../../contexts/AssistantContext';

// Cuanto se espera a que un deep-link resuelva antes de rendirse y mostrar la
// vista normal. Holgado para una recarga lenta, corto para no parecer colgado.
const DEEP_LINK_TIMEOUT_MS = 5000;

const VIEWS = {
  HUB: 'hub',
  AGENT: 'agent',
  OPS: 'ops',
  RUNS: 'runs',
  NEW_AGENT: 'new_agent',
};

const EditModal = ({ project, onClose, onSave }) => {
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    const result = await onSave({ name: name.trim(), description: description.trim() });
    setSaving(false);
    if (result?.success === false) {
      setError(result.error?.[0] || result.error || 'Failed to update project');
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0a0a0a] border border-white/20 rounded-2xl w-full max-w-md p-6 text-white backdrop-blur-xl">
        <h2 className="text-xl font-bold text-white mb-4">Edit Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white mb-2 text-sm">Project Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50"
              maxLength={128}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-white mb-2 text-sm">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50 resize-none"
              rows={3}
              maxLength={2000}
            />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 py-2 bg-primary hover:bg-violet-700 text-white rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProjectDetail = ({ project, onBack, activeTab, onTabChange }) => {
  const { updateProject, currentAccount } = useOrganization();
  const { assistants, loadAssistants, loading: assistantsLoading } = useAssistantContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setView] = useState(VIEWS.HUB);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [workflowCreateMode, setWorkflowCreateMode] = useState(false);
  const [returnView, setReturnView] = useState(null);
  const returnViewRef = React.useRef(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [workflowEditing, setWorkflowEditing] = useState(false);
  const [exiting, setExiting] = useState(false);

  const projectId = project?.project_id;
  const accountId = currentAccount?.id;

  // Keep assistants loaded even if the user lands directly on Workflows/Runs
  useEffect(() => {
    if (projectId) loadAssistants(projectId);
  }, [projectId, loadAssistants]);

  const agentCount = assistants.filter(a => !projectId || a.projectId === projectId).length;

  const navigateTo = React.useCallback((newView, agent, { animate = true } = {}) => {
    const apply = () => {
      if (agent !== undefined) setSelectedAgent(agent);
      // Normal navigation never opens the editor in "create" mode; only the
      // dedicated handleCreateWorkflow path does.
      setWorkflowCreateMode(false);
      setView(newView);
      setWorkflowEditing(false);
      setExiting(false);
    };
    // Restaurar un deep-link no es navegar: no hay una vista de la que salir,
    // asi que la animacion solo anade 220ms de pantalla intermedia.
    if (!animate) {
      apply();
    } else {
      setExiting(true);
      setTimeout(apply, 220);
    }
    // Reflejar el agente abierto en la URL para que el workspace sea
    // deep-linkeable (?agentId=). Al salir del workspace se limpian agentId y
    // section para que refresh/back no re-atrapen al usuario en el agente.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newView === VIEWS.AGENT && agent) {
          next.set('agentId', agent.id);
        } else {
          next.delete('agentId');
          next.delete('section');
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Open the Ops view straight into a blank workflow canvas (new, unsaved).
  const handleCreateWorkflow = React.useCallback(() => {
    setReturnView(view);
    returnViewRef.current = view;
    setSelectedWorkflowId(null);
    setExiting(true);
    setTimeout(() => {
      setWorkflowCreateMode(true);
      setView(VIEWS.OPS);
      setWorkflowEditing(false);
      setExiting(false);
    }, 220);
  }, [view]);

  // Navigate back from workflow editor WITHOUT touching selectedAgent
  const returnFromWorkflowEditor = React.useCallback(() => {
    const dest = returnViewRef.current || VIEWS.HUB;
    returnViewRef.current = null;
    setSelectedWorkflowId(null);
    setWorkflowCreateMode(false);
    setReturnView(null);
    setExiting(true);
    setTimeout(() => {
      setView(dest);
      setWorkflowEditing(false);
      setExiting(false);
    }, 220);
  }, []);

  // Los workflows/runs viven en un unico view global. Los entrypoints legacy
  // (?tab=workflows / ?tab=runs, links viejos o back) redirigen ahi en vez de
  // abrir la lista in-project duplicada. ?tab=assistants = overview del proyecto.
  useEffect(() => {
    if (activeTab === 'workflows' || activeTab === 'runs') {
      setSearchParams({ view: 'Workflows', projectId }, { replace: true });
    } else if (activeTab === 'assistants') {
      setView(VIEWS.HUB);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Entrar directo a crear, desde fuera del proyecto. `?new=agent` abre el
  // asistente de creación y `?new=workflow` un canvas en blanco, los dos ya
  // dentro de este proyecto. Es lo que permite que "New agent" en ⌘K sea una
  // acción y no un enlace a la lista de proyectos, que es donde acababa.
  //
  // El guardia recuerda *qué* orden se cumplió, no *si* se cumplió alguna.
  // Con un booleano, la segunda orden de una misma visita se perdía: pedías un
  // agente, cancelabas —que devuelve al hub sin desmontar esto—, pedías un
  // workflow, y no pasaba nada. Al borrarse `new` de la URL el guardia se
  // suelta, así que cada orden vale una vez y la siguiente vuelve a entrar.
  const consumedIntentRef = React.useRef(null);
  useEffect(() => {
    const intent = searchParams.get('new');
    if (!intent) {
      consumedIntentRef.current = null;
      return;
    }
    if (consumedIntentRef.current === intent) return;
    consumedIntentRef.current = intent;
    // Se saca de la URL en el acto: es una orden que se cumple una vez, y
    // dejarla escrita haría que recargar —o volver atrás— la repitiera.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      },
      { replace: true }
    );
    if (intent === 'workflow') handleCreateWorkflow();
    else setView(VIEWS.NEW_AGENT);
  }, [searchParams, setSearchParams, handleCreateWorkflow]);

  // Deep-link: ?agentId= auto-opens that agent's workspace once assistants load
  // (e.g. from a Zeno card or the Shop's "Open agent"). The id STAYS in the URL
  // so the workspace is deep-linkeable; navigateTo clears it when leaving.
  const openedAgentRef = React.useRef(false);
  const urlAgentId = searchParams.get('agentId');
  // El agente de la URL no existe en este proyecto: se deja de esperar y se cae
  // al hub. Sin esto, un agentId viejo o de otro proyecto dejaria un loader fijo.
  const [deepLinkFailed, setDeepLinkFailed] = useState(false);
  // `loading` arranca en false y solo pasa a true dentro del async de
  // loadAssistants, asi que en el primer render "no esta cargando" y "todavia
  // no empieza" se ven igual. Sin esto se descartaba el deep-link antes de que
  // la peticion saliera.
  const sawLoadingRef = React.useRef(false);
  useEffect(() => {
    if (assistantsLoading) sawLoadingRef.current = true;
  }, [assistantsLoading]);

  useEffect(() => {
    if (!urlAgentId || openedAgentRef.current) return;
    const agent = assistants.find((a) => String(a.id) === String(urlAgentId));
    if (!agent) {
      // Se descarta cuando una carga corrio y termino, o cuando ya hay lista de
      // este proyecto y el agente no esta en ella —loadAssistants vacia la
      // lista al empezar, asi que una lista con contenido es una carga
      // completada. Mientras tanto se sigue esperando.
      const loadFinished = sawLoadingRef.current && !assistantsLoading;
      if (loadFinished || assistants.length > 0) setDeepLinkFailed(true);
      return;
    }
    setDeepLinkFailed(false);
    openedAgentRef.current = true;
    navigateTo(VIEWS.AGENT, agent, { animate: false });
  }, [urlAgentId, assistants, assistantsLoading, navigateTo]);

  // Tope de espera. Las condiciones de arriba asumen que una carga va a ocurrir,
  // y si loadAssistants no llega a correr —sin cuenta confirmada, por ejemplo—
  // no ocurre nunca y el loader se queda fijo. Un spinner que no puede terminar
  // es peor que la pantalla intermedia que vinimos a quitar.
  useEffect(() => {
    if (!urlAgentId || openedAgentRef.current || deepLinkFailed) return undefined;
    const timer = setTimeout(() => setDeepLinkFailed(true), DEEP_LINK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [urlAgentId, deepLinkFailed]);

  // Mientras el deep-link se resuelve no se pinta el hub: es una pantalla que el
  // usuario no pidio y que desaparece sola, o sea un parpadeo. Se muestra un
  // unico estado de carga hasta aterrizar en el workspace.
  const restoringAgent = Boolean(urlAgentId) && !openedAgentRef.current && !deepLinkFailed;

  if (!project) return null;


  const handleBackToHub = () => {
    navigateTo(VIEWS.HUB, null);
    onTabChange?.('assistants');
  };

  // El workspace del agente puede abrirse desde el listado global de Agents
  // (?from=agents). En ese caso el back vuelve a Agents, no al hub del proyecto.
  const cameFromAgents = searchParams.get('from') === 'agents';
  const handleAgentBack = () => {
    if (cameFromAgents) setSearchParams({ view: 'Agents' });
    else handleBackToHub();
  };

  // Salidas del overview a las vistas globales (único lugar donde se browsea),
  // con el proyecto pre-filtrado. El click en un run reciente abre el inspector.
  const goToProjectAgents = () => setSearchParams({ view: 'Agents', projectId });
  const goToProjectWorkflows = () => setSearchParams({ view: 'Workflows', projectId });
  const handleOpenRun = (runId) => { setSelectedRunId(runId); navigateTo(VIEWS.RUNS); };

  const handleSaveProject = async (data) => {
    return updateProject(project.project_id, data);
  };

  const handleOpenWorkflow = (wf) => {
    if (wf?.id) {
      setSelectedWorkflowId(wf.id);
      setReturnView(view);
      returnViewRef.current = view;
      navigateTo(VIEWS.OPS, undefined);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {showEditModal && (
        <EditModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveProject}
        />
      )}

      {/* Top bar — hidden when agent workspace, wizard, or workflow editor is open */}
      {view !== VIEWS.AGENT && view !== VIEWS.NEW_AGENT && !workflowEditing && (
        <div className="flex flex-col gap-4 px-4 pt-4 pb-0 flex-shrink-0 sm:gap-5 sm:px-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="text-xs text-gray-500 hover:text-white transition-colors flex items-center gap-1"
            >
              ← Projects
            </button>
            <span className="text-gray-700">/</span>
            <span className="text-xs text-gray-400">{project.name}</span>
            <button
              onClick={() => setShowEditModal(true)}
              className="ml-1 p-1 hover:bg-white/10 rounded-md transition text-gray-600 hover:text-white"
              title="Edit project"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className="flex-1 min-h-0 overflow-hidden"
        style={{
          opacity: exiting ? 0 : 1,
          transform: exiting ? 'scale(0.97) translateY(6px)' : 'scale(1) translateY(0)',
          transition: 'opacity 200ms ease, transform 200ms ease',
        }}
      >
        {restoringAgent && (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Opening agent…
          </div>
        )}

        {!restoringAgent && view === VIEWS.HUB && (
          <div className="h-full overflow-y-auto px-4 pt-4 pb-8 sm:px-10 sm:pt-5">
            <ProjectOverview
              project={project}
              accountId={accountId}
              agentCount={agentCount}
              onViewAgents={goToProjectAgents}
              onViewWorkflows={goToProjectWorkflows}
              onOpenRun={handleOpenRun}
            />
          </div>
        )}

        {view === VIEWS.AGENT && selectedAgent && (
          <AgentWorkspace
            agent={selectedAgent}
            projectId={project.project_id}
            onBack={handleAgentBack}
            backLabel={cameFromAgents ? 'Back to agents' : 'Back to projects'}
            onOpenWorkflow={handleOpenWorkflow}
            onCreateWorkflow={handleCreateWorkflow}
          />
        )}

        {view === VIEWS.NEW_AGENT && (
          <AssistantsView
            projectId={project.project_id}
            initialCreating={true}
            onEditingChange={(isEditing) => { if (!isEditing) handleBackToHub(); }}
          />
        )}

        {view === VIEWS.OPS && (
          <WorkflowsView
            projectId={project.project_id}
            initialWorkflowId={selectedWorkflowId}
            initialCreating={workflowCreateMode}
            onEditingChange={(isEditing) => setWorkflowEditing(!!isEditing)}
            onBackFromEditor={(selectedWorkflowId || workflowCreateMode) ? returnFromWorkflowEditor : null}
          />
        )}

        {view === VIEWS.RUNS && (
          selectedRunId ? (
            <RunInspector
              runId={selectedRunId}
              projectId={project.project_id}
              onClose={() => setSelectedRunId(null)}
            />
          ) : (
            <div className="h-full overflow-auto px-4 pt-4 pb-8 sm:px-10 sm:pt-5">
              <RunsView
                projectId={project.project_id}
                onSelectRun={(id) => setSelectedRunId(id)}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
